import React from "react";

/**
 * Error Boundary Component
 * Catches JavaScript errors anywhere in the child component tree
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Log error to console (in production, send to error tracking service)
    console.error("Error caught by ErrorBoundary:", error, errorInfo);
    
    this.setState({
      error,
      errorInfo,
    });

    // TODO: Send to error tracking service (Sentry, LogRocket, etc.)
    // if (import.meta.env.PROD) {
    //   sendToErrorTracking(error, errorInfo);
    // }
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render() {
    if (this.state.hasError) {
      return (
        <ErrorFallback
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          onReset={this.handleReset}
        />
      );
    }

    return this.props.children;
  }
}

/**
 * Error Fallback UI
 * Displayed when an error is caught
 */
function ErrorFallback({ error, errorInfo, onReset }) {
  const isDev = import.meta.env.DEV;

  return (
    <div className="container" style={{ paddingTop: "48px" }}>
      <div className="card" style={{ maxWidth: "600px", margin: "0 auto" }}>
        <div style={{ textAlign: "center", padding: "24px" }}>
          <div style={{ fontSize: "64px", marginBottom: "16px" }}>⚠️</div>
          <h1 style={{ margin: "0 0 16px", fontSize: "24px" }}>
            Algo salió mal
          </h1>
          <p className="meta" style={{ marginBottom: "24px" }}>
            Ha ocurrido un error inesperado. Por favor, intenta recargar la página.
          </p>

          <div style={{ display: "flex", gap: "12px", justifyContent: "center", flexWrap: "wrap" }}>
            <button
              className="btn"
              onClick={() => window.location.reload()}
            >
              Recargar página
            </button>
            <button
              className="copy"
              onClick={onReset}
            >
              Intentar de nuevo
            </button>
          </div>

          {isDev && error && (
            <details
              style={{
                marginTop: "24px",
                textAlign: "left",
                background: "#0d1330",
                padding: "16px",
                borderRadius: "8px",
                border: "1px solid #273067",
              }}
            >
              <summary
                style={{
                  cursor: "pointer",
                  fontWeight: "600",
                  marginBottom: "12px",
                }}
              >
                Detalles del error (solo en desarrollo)
              </summary>
              <div style={{ fontSize: "13px", fontFamily: "monospace" }}>
                <p style={{ color: "#ff6b6b", marginBottom: "8px" }}>
                  <strong>Error:</strong> {error.toString()}
                </p>
                {errorInfo && errorInfo.componentStack && (
                  <pre
                    style={{
                      overflow: "auto",
                      padding: "12px",
                      background: "#000",
                      borderRadius: "4px",
                      fontSize: "12px",
                      lineHeight: "1.5",
                    }}
                  >
                    {errorInfo.componentStack}
                  </pre>
                )}
              </div>
            </details>
          )}
        </div>
      </div>
    </div>
  );
}

export default ErrorBoundary;
